import { useState, type CSSProperties, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import {
  Button,
  Callout,
  ErrorBanner,
  Markdown,
  MetricStrip,
  PageHeader,
  Pill,
  Select,
  Surface,
  WorkbenchPanel,
  useToast,
} from '@frm/ui'

import { fetchHelpSection } from '../api/help'
import { formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'
import { getHelpSection, HELP_SECTIONS, type HelpSectionId } from './helpContent'

const PAGE_STYLE: CSSProperties = {
  display: 'grid',
  gap: 18,
  minWidth: 0,
}

const LAYOUT_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '260px minmax(0, 1fr)',
  gap: 16,
  alignItems: 'start',
}

const NAV_STYLE: CSSProperties = {
  display: 'grid',
  gap: 14,
  position: 'sticky',
  top: 120,
}

const STACK_STYLE: CSSProperties = {
  display: 'grid',
  gap: 12,
}

const ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted, #94a3b8)',
}

const GUIDE_PANEL_STYLE: CSSProperties = {
  boxShadow: 'var(--frm-workbench-shadow-panel)',
}

const WELL_STYLE: CSSProperties = {
  background: 'var(--frm-workbench-well, #0B1020)',
  border: '1px solid var(--frm-line-subtle, rgba(148, 163, 184, 0.14))',
  borderRadius: 13,
}

const GUIDE_TEXT_STYLE: CSSProperties = {
  color: 'var(--frm-text-secondary)',
  fontSize: 13.5,
  lineHeight: 1.6,
}

const GUIDE_TWO_COLUMN_STYLE: CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
}

const GUIDE_THREE_COLUMN_STYLE: CSSProperties = {
  display: 'grid',
  gap: 13,
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
}

const FLOW_GRID_STYLE: CSSProperties = {
  alignItems: 'stretch',
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'minmax(0, 1fr) 24px minmax(0, 1.35fr) 24px minmax(0, 1fr) 24px minmax(0, 1fr)',
}

const DEFAULT_HELP_SECTION: HelpSectionId = 'context'

const HELP_SECTION_META: Record<HelpSectionId, { number: string; meta: string; source: string }> = {
  start: {
    number: '1',
    meta: 'принципы · команды',
    source: 'docs/concept.md + skills/common/commands.md',
  },
  projects: {
    number: '2',
    meta: 'структура · архитектура',
    source: 'docs/architecture.md + context.md',
  },
  sprints: {
    number: '3',
    meta: 'roadmap · статусы',
    source: 'skills/common/sprints.md + docs/concept.md §2.7',
  },
  context: {
    number: '4',
    meta: 'алгебра · бюджеты',
    source: 'docs/concept.md §3 + skills/common/context-management.md',
  },
}

const HELP_SECTION_OPTIONS = HELP_SECTIONS.map((entry) => ({
  value: entry.id,
  label: `${HELP_SECTION_META[entry.id].number}. ${entry.title}`,
}))

const THRESHOLDS = [
  { value: 60, label: 'наблюдение', tone: 'info' as const },
  { value: 70, label: 'предупредить', tone: 'warning' as const },
  { value: 80, label: 'предложить checkpoint', tone: 'danger' as const },
  { value: 85, label: 'принудительный checkpoint', tone: 'danger' as const },
]

function isHelpSectionId(value: string | null): value is HelpSectionId {
  return HELP_SECTIONS.some((section) => section.id === value)
}

function thresholdState(percent: number): string {
  const active = [...THRESHOLDS].reverse().find((threshold) => percent >= threshold.value)
  return active ? `${active.value}% · ${active.label}` : 'окно в норме'
}

function GuidePanel({
  title,
  kicker,
  description,
  children,
  className,
}: {
  title: string
  kicker?: string
  description?: string
  children: ReactNode
  className?: string
}) {
  return (
    <WorkbenchPanel title={title} description={description} style={GUIDE_PANEL_STYLE} className={className}>
      <div style={STACK_STYLE}>
        {kicker ? (
          <span style={{ color: 'var(--frm-accent)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
            {kicker}
          </span>
        ) : null}
        {children}
      </div>
    </WorkbenchPanel>
  )
}

function LayerChip({
  children,
  color,
}: {
  children: ReactNode
  color: string
}) {
  return (
    <span
      style={{
        alignItems: 'center',
        background: 'var(--frm-workbench-panel-2)',
        border: `1px solid ${color}44`,
        borderRadius: 8,
        color,
        display: 'inline-flex',
        fontSize: 11.5,
        gap: 6,
        padding: '5px 10px',
      }}
    >
      <span style={{ background: color, borderRadius: '50%', height: 6, width: 6 }} />
      {children}
    </span>
  )
}

function FlowCard({
  title,
  description,
  tone,
  children,
}: {
  title: string
  description: string
  tone: string
  children?: ReactNode
}) {
  return (
    <div style={{ ...WELL_STYLE, borderColor: `${tone}44`, display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0, padding: 16 }}>
      <span style={{ background: `${tone}22`, border: `1px solid ${tone}55`, borderRadius: 8, color: tone, display: 'grid', fontWeight: 900, height: 30, placeItems: 'center', width: 30 }}>
        •
      </span>
      <span style={{ color: 'var(--frm-text-primary)', fontSize: 14, fontWeight: 800 }}>{title}</span>
      <span style={{ color: 'var(--frm-text-muted)', fontSize: 12, lineHeight: 1.5 }}>{description}</span>
      {children}
    </div>
  )
}

function ArrowCell() {
  return (
    <div className="help-context-arrow" style={{ alignItems: 'center', color: 'var(--frm-text-muted)', display: 'flex', fontSize: 20, fontWeight: 800, justifyContent: 'center' }}>
      →
    </div>
  )
}

function WindowLayer({
  title,
  note,
  color,
  mono,
  accent,
}: {
  title: string
  note: string
  color: string
  mono?: boolean
  accent?: boolean
}) {
  return (
    <div
      style={{
        alignItems: 'center',
        background: accent ? 'linear-gradient(135deg, rgba(56,189,248,.14), rgba(99,102,241,.10))' : 'var(--frm-workbench-panel-2)',
        border: `1px solid ${color}44`,
        borderRadius: 10,
        display: 'flex',
        gap: 11,
        padding: '12px 14px',
      }}
    >
      <span style={{ background: color, borderRadius: '50%', flexShrink: 0, height: 8, width: 8 }} />
      <span style={{ color: 'var(--frm-text-secondary)', flex: 1, fontSize: 13, fontWeight: 700 }}>{title}</span>
      <span style={{ ...(mono ? MONO_NUMERIC_STYLE : {}), color, fontSize: mono ? 11 : 11.5 }}>{note}</span>
    </div>
  )
}

function Bullet({ children }: { children: ReactNode }) {
  return (
    <li style={{ alignItems: 'flex-start', display: 'flex', gap: 11 }}>
      <span style={{ background: 'rgba(56,189,248,.12)', border: '1px solid rgba(56,189,248,.28)', borderRadius: 6, color: 'var(--frm-accent)', display: 'grid', flexShrink: 0, fontSize: 14, fontWeight: 900, height: 22, placeItems: 'center', width: 22 }}>
        →
      </span>
      <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 13 }}>{children}</span>
    </li>
  )
}

function HelpNavButton({
  section,
  active,
  onSelect,
}: {
  section: HelpSectionId
  active: boolean
  onSelect: (section: HelpSectionId) => void
}) {
  const meta = HELP_SECTION_META[section]
  const title = getHelpSection(section).title
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(section)}
      style={{
        alignItems: 'center',
        background: active ? 'rgba(56,189,248,.08)' : 'transparent',
        border: `1px solid ${active ? 'rgba(56,189,248,.34)' : 'transparent'}`,
        borderRadius: 11,
        color: 'inherit',
        cursor: 'pointer',
        display: 'flex',
        fontFamily: 'var(--frm-font-ui, Manrope, system-ui)',
        gap: 12,
        minHeight: 54,
        padding: '11px 12px',
        textAlign: 'left',
        transition: 'background 120ms var(--frm-easing-standard), border-color 120ms var(--frm-easing-standard)',
        width: '100%',
      }}
    >
      <span
        style={{
          background: active ? 'linear-gradient(135deg, var(--frm-accent), var(--frm-accent-2))' : 'var(--frm-workbench-panel-2)',
          border: active ? 'none' : '1px solid var(--frm-line-subtle)',
          borderRadius: 8,
          color: active ? 'var(--frm-text-on-accent)' : 'var(--frm-text-muted)',
          display: 'grid',
          flexShrink: 0,
          fontFamily: 'var(--frm-font-mono)',
          fontSize: 13,
          fontWeight: 700,
          height: 26,
          placeItems: 'center',
          width: 26,
        }}
      >
        {meta.number}
      </span>
      <span style={{ display: 'grid', flex: 1, gap: 2, minWidth: 0 }}>
        <span style={{ color: active ? 'var(--frm-text-primary)' : 'var(--frm-text-secondary)', fontSize: 14, fontWeight: 800 }}>
          {title}
        </span>
        <span style={{ color: 'var(--frm-text-muted)', fontSize: 11, lineHeight: 1.35 }}>{meta.meta}</span>
      </span>
      <span aria-hidden style={{ color: active ? 'var(--frm-accent)' : 'var(--frm-text-muted)', fontSize: 15 }}>
        ›
      </span>
    </button>
  )
}

function SourceMarkdownPanel({
  section,
  markdown,
  loading,
}: {
  section: HelpSectionId
  markdown: string
  loading: boolean
}) {
  return (
    <GuidePanel
      title="Исходный markdown"
      description={`Read-only выдержка из ${HELP_SECTION_META[section].source}.`}
    >
      {loading ? (
        <Surface padding="md" variant="subtle"><span style={MUTED_STYLE}>Загружаем markdown из docs/skills...</span></Surface>
      ) : (
        <Markdown source={markdown} />
      )}
    </GuidePanel>
  )
}

function StepRail({
  steps,
}: {
  steps: Array<{ n: string; title: string; text: string; tone?: string }>
}) {
  return (
    <div style={{ display: 'grid', gap: 0 }}>
      {steps.map((step, index) => {
        const tone = step.tone ?? '#38BDF8'
        return (
          <div key={step.n} style={{ alignItems: 'stretch', display: 'flex', gap: 13 }}>
            <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <span
                style={{
                  background: `${tone}22`,
                  border: `1px solid ${tone}55`,
                  borderRadius: '50%',
                  color: tone,
                  display: 'grid',
                  fontFamily: 'var(--frm-font-mono)',
                  fontSize: 12,
                  fontWeight: 800,
                  height: 28,
                  placeItems: 'center',
                  width: 28,
                }}
              >
                {step.n}
              </span>
              {index < steps.length - 1 ? <span style={{ background: `${tone}44`, flex: 1, minHeight: 18, width: 2 }} /> : null}
            </div>
            <div style={{ display: 'grid', gap: 4, paddingBottom: index < steps.length - 1 ? 16 : 0 }}>
              <span style={{ color: 'var(--frm-text-primary)', fontSize: 13.5, fontWeight: 800 }}>{step.title}</span>
              <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 12.5 }}>{step.text}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function CommandGrid({
  commands,
}: {
  commands: Array<{ command: string; title: string; text: string; tone?: string }>
}) {
  return (
    <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
      {commands.map((item) => {
        const tone = item.tone ?? 'var(--frm-accent)'
        return (
          <Surface key={item.command} padding="md" variant="subtle">
            <div style={{ display: 'grid', gap: 8 }}>
              <code style={{ ...MONO_NUMERIC_STYLE, color: tone, fontSize: 13, fontWeight: 800 }}>{item.command}</code>
              <span style={{ color: 'var(--frm-text-primary)', fontSize: 13.5, fontWeight: 800 }}>{item.title}</span>
              <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 12.5 }}>{item.text}</span>
            </div>
          </Surface>
        )
      })}
    </div>
  )
}

function StartGuide({
  sourceMarkdown,
  loading,
}: {
  sourceMarkdown: string
  loading: boolean
}) {
  return (
    <div style={STACK_STYLE}>
      <GuidePanel title="Быстрый старт и принципы" kicker="Подраздел 1 · docs/concept.md + skills/common/commands.md">
        <Callout tone="info">
          «Команда» — рабочая owner-панель AI-агентов ФРМ.AI. Здесь видны агенты, проекты, спринты, справка по контексту и переходы в web-chat.
        </Callout>
      </GuidePanel>

      <GuidePanel title="Рабочий цикл">
        <StepRail
          steps={[
            { n: '1', title: 'Выбрать проект и спринт', text: 'Контекст агента привязан к узлу vault и текущему roadmap. Смена проекта или спринта проходит через checkpoint или явный сброс.' },
            { n: '2', title: 'Поставить задачу агенту', text: 'Задача приходит из Telegram или web-chat. Агент отвечает в рамках собранного context.session.' },
            { n: '3', title: 'Проверить результат', text: 'Код проверяется тестом, сборкой или smoke-прогоном. Знание проекта фиксируется в markdown.' },
            { n: '4', title: 'Сохранить checkpoint', text: 'Результат раскладывается в roadmap, chats, context или docs, без записи секретов.' },
          ]}
        />
      </GuidePanel>

      <GuidePanel title="Основные принципы">
        <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
          {[
            ['Vault — источник истины', 'Агенты и движки заменяемы; постоянное знание живёт в markdown и git.'],
            ['Read-only по умолчанию', 'Запись выполняется только через подтверждаемый flow: checkpoint или edit → diff → confirm.'],
            ['Секреты не попадают в UI', 'Токены, ключи и пароли scrubbed на backend и не сохраняются в vault-документах.'],
            ['Деструктив — только owner', 'Restart, release, deploy, удаление и опасные операции требуют отдельной owner-команды.'],
          ].map(([title, text]) => (
            <Surface key={title} padding="md" variant="subtle">
              <div style={STACK_STYLE}>
                <span style={{ color: 'var(--frm-text-primary)', fontSize: 14, fontWeight: 800 }}>{title}</span>
                <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 12.5 }}>{text}</span>
              </div>
            </Surface>
          ))}
        </div>
      </GuidePanel>

      <GuidePanel title="Команды Telegram и web-chat" description="Список согласован с platform skill `commands.md`; несуществующие команды не показываются.">
        <CommandGrid
          commands={[
            { command: '/status', title: 'Статус сессии', text: 'Проект, спринт, режим, заполнение окна, активные задачи и важные невыпущенные изменения.' },
            { command: '/project list', title: 'Выбор проекта', text: 'Дерево доступных vault-проектов с inline-навигацией.' },
            { command: '/sprint', title: 'Текущий roadmap', text: 'Цель, статусы задач, проверка закрытия и ближайший следующий шаг.' },
            { command: '/checkpoint', title: 'Сохранить знание', text: 'Подготовить план записи в roadmap, chats, context/docs или repo и дождаться подтверждения.' },
            { command: '/recall <запрос>', title: 'Найти прошлое решение', text: 'Поиск по context, docs, roadmap и chats текущего проекта без показа секретов.' },
            { command: '/new', title: 'Новая сессия', text: 'Завершить текущую через save/delete checkpoint flow и выбрать новый проект или спринт.' },
          ]}
        />
      </GuidePanel>

      <SourceMarkdownPanel section="start" markdown={sourceMarkdown} loading={loading} />
    </div>
  )
}

function ProjectsGuide({
  sourceMarkdown,
  loading,
}: {
  sourceMarkdown: string
  loading: boolean
}) {
  return (
    <div style={STACK_STYLE}>
      <GuidePanel title="Проекты и архитектура" kicker="Подраздел 2 · docs/architecture.md + context.md">
        <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
          <Surface padding="md" variant="subtle">
            <div style={STACK_STYLE}>
              <span style={{ color: 'var(--frm-success)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Структура проекта</span>
              <div style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-secondary)', fontSize: 12.5, lineHeight: 1.95 }}>
                <div style={{ color: 'var(--frm-success)' }}>vault-new/frm.ai/ai.agents/</div>
                <div style={{ paddingLeft: 14 }}>├─ context.md <span style={{ color: 'var(--frm-text-muted)', fontFamily: 'var(--frm-font-ui)' }}>карта проекта</span></div>
                <div style={{ paddingLeft: 14 }}>├─ product/ · design/ · devops/</div>
                <div style={{ paddingLeft: 14 }}>├─ docs/ <span style={{ color: 'var(--frm-text-muted)', fontFamily: 'var(--frm-font-ui)' }}>архитектура и концепция</span></div>
                <div style={{ paddingLeft: 14 }}>├─ skills/ <span style={{ color: 'var(--frm-text-muted)', fontFamily: 'var(--frm-font-ui)' }}>проектные скилы</span></div>
                <div style={{ paddingLeft: 14 }}>├─ repo/ <span style={{ color: 'var(--frm-text-muted)', fontFamily: 'var(--frm-font-ui)' }}>отдельные git-контуры</span></div>
                <div style={{ paddingLeft: 14 }}>└─ sprints/&lt;name&gt;/roadmap.md</div>
              </div>
            </div>
          </Surface>
          <Surface padding="md" variant="subtle">
            <div style={STACK_STYLE}>
              <span style={{ color: 'var(--frm-text-muted)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Иерархия контекста</span>
              <p style={{ ...GUIDE_TEXT_STYLE, margin: 0 }}>
                Папка с <code style={MONO_NUMERIC_STYLE}>context.md</code> становится узлом. Подпроект наследует контекст всех надпроектов от корня, но факты не дублируются вверх.
              </p>
              <div style={{ ...WELL_STYLE, color: 'var(--frm-text-muted)', fontFamily: 'var(--frm-font-mono)', fontSize: 12, lineHeight: 1.8, padding: '13px 15px' }}>
                <div><span style={{ color: 'var(--frm-accent)' }}>vault</span> → frm.ai → ai.agents → sprint</div>
                <div>context.subproject = project + надпроекты</div>
              </div>
            </div>
          </Surface>
        </div>
      </GuidePanel>

      <GuidePanel title="Как агент работает с проектом">
        <StepRail
          steps={[
            { n: '1', title: 'Собирает карту', text: 'Читает context.md проекта, карты product/design/devops, docs и текущий roadmap.' },
            { n: '2', title: 'Разделяет знание и код', text: 'Vault markdown — память проекта; nested repo/ — отдельные кодовые git-репозитории.' },
            { n: '3', title: 'Пишет только в выбранный узел', text: 'Запись в другой проект или вверх по дереву требует явного поручения или checkpoint-плана.' },
            { n: '4', title: 'Фиксирует результат в sprint journal', text: 'Ход спринта отражается в roadmap, подробности — в chats/ конспекте.' },
          ]}
        />
      </GuidePanel>

      <GuidePanel title="Команды проектов">
        <CommandGrid
          commands={[
            { command: '/project list', title: 'Навигация по vault', text: 'Открывает дерево проектов и подпроектов; ручной путь остаётся запасным вариантом.' },
            { command: '/project <имя>', title: 'Прямой переход', text: 'Переключает проект после checkpoint или явного отказа от сохранения текущей сессии.' },
            { command: '/status', title: 'Рабочий контур', text: 'Показывает текущий проект, спринт и важные невыпущенные изменения.' },
            { command: '/new', title: 'Новый контекст', text: 'Завершает текущую сессию через save/delete flow и даёт выбрать новый проект или спринт.' },
          ]}
        />
      </GuidePanel>

      <SourceMarkdownPanel section="projects" markdown={sourceMarkdown} loading={loading} />
    </div>
  )
}

function SprintsGuide({
  sourceMarkdown,
  loading,
}: {
  sourceMarkdown: string
  loading: boolean
}) {
  return (
    <div style={STACK_STYLE}>
      <GuidePanel title="Работа со спринтами" kicker="Подраздел 3 · skills/common/sprints.md + docs/concept.md §2.7">
        <div style={{ alignItems: 'stretch', background: 'linear-gradient(135deg, rgba(251,191,36,.08), rgba(251,191,36,.03))', border: '1px solid rgba(251,191,36,.22)', borderRadius: 13, display: 'flex', gap: 16, padding: '18px 20px' }}>
          <span style={{ background: 'linear-gradient(180deg,#FBBF24,#FB923C)', borderRadius: 999, flexShrink: 0, width: 4 }} />
          <p style={{ color: 'var(--frm-text-secondary)', fontSize: 16, fontWeight: 650, lineHeight: 1.55, margin: 0 }}>
            <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-warning)' }}>roadmap.md</code> — источник истины спринта. В нём живут цель, задачи, критерии, проверка закрытия и журнал решений.
          </p>
        </div>
      </GuidePanel>

      <GuidePanel title="Статусы задач">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 11 }}>
          {[
            ['[ ]', 'не начато', 'var(--frm-text-muted)'],
            ['[~]', 'в работе', 'var(--frm-accent)'],
            ['[x]', 'готово', 'var(--frm-success)'],
            ['[!]', 'заблокировано', 'var(--frm-danger)'],
          ].map(([code, label, color]) => (
            <span key={code} style={{ alignItems: 'center', background: 'var(--frm-workbench-well)', border: '1px solid var(--frm-line-subtle)', borderRadius: 10, color: 'var(--frm-text-secondary)', display: 'inline-flex', fontSize: 13, gap: 9, padding: '10px 14px' }}>
              <code style={{ ...MONO_NUMERIC_STYLE, color }}>{code}</code>
              {label}
            </span>
          ))}
        </div>
      </GuidePanel>

      <GuidePanel title="Контракт roadmap">
        <StepRail
          steps={[
            { n: '1', title: 'Scope и не входит', text: 'Спринт фиксирует границы результата до начала реализации.' },
            { n: '2', title: 'Задачи с критериями', text: 'Каждая todo-задача имеет проверяемый критерий готовности.' },
            { n: '3', title: 'Проверка закрытия', text: 'Спринт закрывается по проверяемому улучшению, а не по внутренней активности.' },
            { n: '4', title: 'Журнал и chats/', text: 'Каждая значимая запись ссылается на конспект сессии.' },
          ]}
        />
      </GuidePanel>

      <GuidePanel title="Команды спринтов">
        <CommandGrid
          commands={[
            { command: '/sprint', title: 'Показать roadmap', text: 'Цель, статусы задач, проверка закрытия и ближайший следующий шаг.' },
            { command: '/sprint <имя>', title: 'Переключить спринт', text: 'Меняет активный sprint после checkpoint или явного сброса текущей сессии.' },
            { command: '/checkpoint', title: 'Зафиксировать ход', text: 'Обновляет roadmap/journal и создаёт конспект после подтверждения владельца.' },
            { command: '/recall <запрос>', title: 'Найти прошлое решение', text: 'Ищет по roadmap и chats текущего проекта, не показывая секреты.' },
          ]}
        />
      </GuidePanel>

      <SourceMarkdownPanel section="sprints" markdown={sourceMarkdown} loading={loading} />
    </div>
  )
}

function ConversationGrowth() {
  const turns: Array<[string, string[]]> = [
    ['ход 1', ['контекст из vault', 'Q1', 'A1']],
    ['ход 2', ['контекст из vault', 'Q1', 'A1', 'Q2', 'A2']],
    ['ход 3', ['контекст из vault', 'Q1', 'A1', 'Q2', 'A2', 'Q3', 'A3']],
  ]
  return (
    <GuidePanel title="История диалога — это не память, а часть контекста" description="и она тащится в каждый следующий запрос">
      <p style={GUIDE_TEXT_STYLE}>
        Диалог только выглядит непрерывным. На деле каждый следующий запрос заново несёт всю предыдущую переписку как часть контекста. Поэтому с каждым ходом пакет растёт.
      </p>
      <div style={STACK_STYLE}>
        {turns.map(([label, parts]) => (
          <div key={label} style={{ ...WELL_STYLE, alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, padding: '13px 15px' }}>
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', flexShrink: 0, fontSize: 11, fontWeight: 700, width: 48 }}>{label}</span>
            {(parts as string[]).map((part, index) => {
              const isVault = part.includes('vault')
              const isQuestion = part.startsWith('Q')
              const color = isVault ? 'var(--frm-text-muted)' : isQuestion ? 'var(--frm-accent)' : 'var(--frm-success)'
              return (
                <span key={`${part}-${index}`} style={{ background: isVault ? 'var(--frm-workbench-panel-2)' : `${isQuestion ? 'rgba(56,189,248,.10)' : 'rgba(52,211,153,.10)'}`, border: `1px solid ${isQuestion ? 'rgba(56,189,248,.26)' : isVault ? 'rgba(148,163,184,.18)' : 'rgba(52,211,153,.26)'}`, borderRadius: 8, color, fontSize: 11.5, fontWeight: isVault ? 500 : 800, padding: '5px 11px' }}>
                  {part}
                </span>
              )
            })}
          </div>
        ))}
      </div>
      <Surface padding="md" variant="subtle">
        <span style={GUIDE_TEXT_STYLE}>
          Чем длиннее диалог, тем больше окна съедает переписка. Именно поэтому контекст держат в рамках бюджета, переписку сворачивают через <code style={MONO_NUMERIC_STYLE}>/checkpoint</code>, а новый чистый контур начинают через <code style={MONO_NUMERIC_STYLE}>/new</code>.
        </span>
      </Surface>
    </GuidePanel>
  )
}

function WhyControl() {
  const cards = [
    ['Стоимость и скорость', 'Весь контекст уходит модели на каждом запросе. Раздутое окно дороже, медленнее и быстрее упирается в лимит.'],
    ['Качество ответа', 'Что вошло в окно — то агент и знает. Неполный или грязный контекст даёт неверный ответ.'],
    ['Управляемость', 'Checkpoint превращает временную переписку в структурированное знание vault.'],
  ]
  return (
    <GuidePanel title="Почему контекст важно контролировать">
      <div className="help-context-three" style={GUIDE_THREE_COLUMN_STYLE}>
        {cards.map(([title, text]) => (
          <Surface key={title} padding="md" variant="subtle">
            <div style={STACK_STYLE}>
              <span style={{ color: 'var(--frm-text-primary)', fontSize: 14, fontWeight: 800 }}>{title}</span>
              <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 12.5 }}>{text}</span>
            </div>
          </Surface>
        ))}
      </div>
    </GuidePanel>
  )
}

function ContextAlgebra() {
  return (
    <GuidePanel title="Как собирается контекст" description="алгебра — каждый уровень включает предыдущий">
      <div style={{ ...WELL_STYLE, display: 'grid', gap: 10, padding: 18 }}>
        <WindowLayer title="context.session" note="переписка + sprint.context + скилы" color="#34D399" mono />
        <div style={{ paddingLeft: 22 }}>
          <WindowLayer title="sprint.context" note="context.subproject + текущий спринт" color="#FBBF24" mono />
        </div>
        <div style={{ paddingLeft: 44 }}>
          <WindowLayer title="context.subproject" note="+ context.md всех надпроектов" color="#818CF8" mono />
        </div>
        <div style={{ paddingLeft: 66 }}>
          <WindowLayer title="context.project" note="context.md + docs + zones" color="#38BDF8" mono />
        </div>
      </div>
      <Surface padding="md" variant="subtle">
        <span style={GUIDE_TEXT_STYLE}>
          Для `misc` вместо <code style={MONO_NUMERIC_STYLE}>sprint.context</code> берётся <code style={MONO_NUMERIC_STYLE}>context.subproject</code>. `repo/`, `chats/`, `specs/` и `docs/reference/` не входят в автосборку.
        </span>
      </Surface>
    </GuidePanel>
  )
}

function ContextMeter() {
  const { toast } = useToast()
  const [messages, setMessages] = useState(2)
  const skillsPct = 12
  const sprintPct = 26
  const conversationPct = Math.min(48, messages * 7)
  const percent = Math.min(100, skillsPct + sprintPct + conversationPct)

  const addMessage = () => {
    setMessages((current) => {
      const next = current + 1
      const currentPercent = Math.min(100, skillsPct + sprintPct + Math.min(48, current * 7))
      const nextPercent = Math.min(100, skillsPct + sprintPct + Math.min(48, next * 7))
      const threshold = [...THRESHOLDS].reverse().find((item) => currentPercent < item.value && nextPercent >= item.value)
      if (threshold) {
        toast({
          title: 'Контекст растёт',
          description: `Достигнут порог ${threshold.value}%: ${threshold.label}.`,
          tone: threshold.value >= 80 ? 'warning' : 'info',
          duration: 5000,
        })
      }
      return next
    })
  }

  const checkpoint = () => {
    setMessages(0)
    toast({
      title: 'Чекпоинт записан',
      description: 'sprints/.../chats/ · окно освобождено.',
      tone: 'warning',
      duration: 4000,
    })
  }

  const newSession = () => {
    setMessages(0)
    toast({
      title: 'Новая сессия',
      description: 'Демо-окно очищено; реальная команда /new сначала запускает save/delete checkpoint flow.',
      tone: 'info',
      duration: 4000,
    })
  }

  return (
    <GuidePanel title="Окно контекста сессии" description="добавляйте сообщения и смотрите, как срабатывают триггеры checkpoint">
      <div style={STACK_STYLE}>
        <div style={ROW_STYLE}>
          <span style={{ color: 'var(--frm-text-muted)', fontSize: 12 }}>заполнение окна</span>
          <Pill tone={percent >= 80 ? 'danger' : percent >= 70 ? 'warning' : 'success'}>{thresholdState(percent)}</Pill>
        </div>
        <div
          aria-label="Заполнение окна контекста"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          style={{
            background: 'var(--frm-workbench-well-2, #080C16)',
            border: '1px solid var(--frm-line-subtle, rgba(148, 163, 184, 0.14))',
            borderRadius: 11,
            height: 38,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', height: '100%', width: `${percent}%` }}>
            <span style={{ background: '#38BDF8', width: `${skillsPct / percent * 100}%` }} />
            <span style={{ background: '#818CF8', width: `${sprintPct / percent * 100}%` }} />
            <span style={{ background: percent >= 80 ? '#FB923C' : '#34D399', width: `${conversationPct / percent * 100}%` }} />
          </div>
          {[60, 70, 80, 85].map((mark) => (
            <span key={mark} style={{ background: 'rgba(248,250,252,.42)', height: '100%', left: `${mark}%`, position: 'absolute', top: 0, width: 1 }} />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <LayerChip color="#38BDF8">скилы · {skillsPct}%</LayerChip>
          <LayerChip color="#818CF8">sprint.context · {sprintPct}%</LayerChip>
          <LayerChip color={percent >= 80 ? '#FB923C' : '#34D399'}>переписка · {conversationPct}%</LayerChip>
          <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11, marginLeft: 'auto' }}>{messages} сообщ. в сессии</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" variant="secondary" onClick={addMessage}>
            Сообщение в чат
          </Button>
          <Button size="sm" variant="secondary" onClick={checkpoint}>
            /checkpoint
          </Button>
          <Button size="sm" variant="primary" onClick={newSession}>
            /new
          </Button>
          <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11, marginLeft: 'auto', alignSelf: 'center' }}>старт сессии ≤ 60% окна</span>
        </div>
      </div>
    </GuidePanel>
  )
}

function Budgets() {
  return (
    <GuidePanel title="Бюджеты" kicker="§3.2" description="инструмент управления окном, а не лимит на знание">
      <div className="help-context-three" style={GUIDE_THREE_COLUMN_STYLE}>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <span style={{ color: 'var(--frm-text-primary)', fontSize: 13, fontWeight: 800 }}>Файлы проекта/docs</span>
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)', fontSize: 24 }}>4–6k / 6–10k</span>
            <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 12 }}>`context.md` и карты зон / каждый `docs/*.md`</span>
          </div>
        </Surface>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <span style={{ color: 'var(--frm-text-primary)', fontSize: 13, fontWeight: 800 }}>sprint.context</span>
            <span style={{ ...MONO_NUMERIC_STYLE, color: '#A5B4FC', fontSize: 24 }}>≤ 20–30%</span>
            <div style={{ background: 'var(--frm-workbench-well-2, #080C16)', borderRadius: 999, height: 8, overflow: 'hidden' }}><div style={{ background: 'linear-gradient(90deg,#818CF8,#6366F1)', height: '100%', width: '28%' }} /></div>
          </div>
        </Surface>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <span style={{ color: 'var(--frm-text-primary)', fontSize: 13, fontWeight: 800 }}>context.session на старте</span>
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-success)', fontSize: 24 }}>≤ 60%</span>
            <div style={{ background: 'var(--frm-workbench-well-2, #080C16)', borderRadius: 999, height: 8, overflow: 'hidden' }}><div style={{ background: 'linear-gradient(90deg,#34D399,#10B981)', height: '100%', width: '60%' }} /></div>
          </div>
        </Surface>
      </div>
      <Surface padding="md" variant="subtle">
        <span style={GUIDE_TEXT_STYLE}>Базовое окно транспорта для long-context runtime — <code style={MONO_NUMERIC_STYLE}>1M</code> токенов; при необходимости оно сужается через <code style={MONO_NUMERIC_STYLE}>CONTEXT_WINDOW_TOKENS</code>. Превышение бюджета → агент предлагает дробление или вынос деталей в `docs/reference/`, а не молча обрезает. На `docs/reference/`, `chats/` и `specs/` бюджет не распространяется.</span>
      </Surface>
    </GuidePanel>
  )
}

function OverflowAndTriggers() {
  return (
    <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
      <GuidePanel title="Переполнение · порядок вытеснения" kicker="§3.3">
        <div style={STACK_STYLE}>
          {[
            ['1', 'Дальние надпроекты', ''],
            ['2', 'context.md, карты зон и docs/ проекта', 'до концепции и решений'],
            ['3', 'Спринт', 'до roadmap.md'],
          ].map(([n, title, note]) => (
            <div key={n} style={{ alignItems: 'flex-start', display: 'flex', gap: 13 }}>
              <span style={{ background: 'rgba(251,113,133,.12)', border: '1px solid rgba(251,113,133,.32)', borderRadius: '50%', color: 'var(--frm-danger)', display: 'grid', flexShrink: 0, fontWeight: 800, height: 26, placeItems: 'center', width: 26 }}>{n}</span>
              <span style={GUIDE_TEXT_STYLE}><strong style={{ color: 'var(--frm-text-secondary)' }}>{title}</strong>{note ? <><br /><span style={MUTED_STYLE}>{note}</span></> : null}</span>
            </div>
          ))}
          <Surface padding="md" variant="subtle">
            <span style={GUIDE_TEXT_STYLE}><strong style={{ color: 'var(--frm-success)' }}>Переписка не вытесняется никогда.</strong> Вместо обрезки переписки делается checkpoint.</span>
          </Surface>
        </div>
      </GuidePanel>
      <GuidePanel title="Триггеры checkpoint" kicker="§3.4">
        <Surface padding="md" variant="subtle">
          <span style={GUIDE_TEXT_STYLE}><Pill tone="info" size="sm">MVP</Pill> Ручной <code style={MONO_NUMERIC_STYLE}>/checkpoint</code> — дисциплина чекпоинта в конце сессии.</span>
        </Surface>
        <div style={STACK_STYLE}>
          {[
            ['~70%', 'предупреждение', '#FCD34D'],
            ['~80%', 'предложение /checkpoint', '#FB923C'],
            ['~85%', 'принудительный + новая сессия', '#FB7185'],
          ].map(([pct, label, color]) => (
            <div key={pct} style={{ ...WELL_STYLE, alignItems: 'center', borderColor: `${color}44`, display: 'flex', gap: 12, padding: '11px 14px' }}>
              <span style={{ ...MONO_NUMERIC_STYLE, color, fontSize: 15, fontWeight: 700, width: 48 }}>{pct}</span>
              <span style={{ background: 'rgba(148,163,184,.2)', height: 22, width: 1 }} />
              <span style={GUIDE_TEXT_STYLE}>{label}</span>
            </div>
          ))}
        </div>
      </GuidePanel>
    </div>
  )
}

function ContextResetGuide() {
  return (
    <GuidePanel title="Обнуление контекста — зачем и как" description="источник истины — vault, поэтому обнулять безопасно">
      <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <span style={{ color: 'var(--frm-warning)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Зачем</span>
            <ul style={{ display: 'grid', gap: 9, listStyle: 'none', margin: 0, padding: 0 }}>
              <Bullet>Длинная переписка засоряет окно и снижает точность — старое перекрывает актуальное.</Bullet>
              <Bullet>Знание уже зафиксировано в vault через checkpoint — терять нечего.</Bullet>
              <Bullet>Свежая сборка из vault дешевле и точнее раздутой истории.</Bullet>
            </ul>
          </div>
        </Surface>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <span style={{ color: 'var(--frm-success)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Как · в этом проекте</span>
            <div style={STACK_STYLE}>
              <span style={GUIDE_TEXT_STYLE}><code style={MONO_NUMERIC_STYLE}>/checkpoint</code> — свернуть переписку в конспект → vault.</span>
              <span style={GUIDE_TEXT_STYLE}><code style={MONO_NUMERIC_STYLE}>/new</code> — завершить текущую сессию через save/delete flow и начать новый контур.</span>
            </div>
            <div style={{ ...WELL_STYLE, color: 'var(--frm-text-muted)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, lineHeight: 1.7, padding: '13px 15px' }}>
              <div><span style={{ color: 'var(--frm-success)' }}>$</span> /new</div>
              <div>→ checkpoint/save-delete выбор</div>
              <div style={{ color: 'var(--frm-success)' }}>✓ новый context.session собирается из vault</div>
            </div>
          </div>
        </Surface>
      </div>
    </GuidePanel>
  )
}

function ContextGuide({
  sourceMarkdown,
  loading,
}: {
  sourceMarkdown: string
  loading: boolean
}) {
  return (
    <div style={STACK_STYLE}>
      <GuidePanel title="Контекст" kicker="Подраздел 4 · из docs/concept.md §3 + skills/common/context-management.md">
        <Callout
          tone="info"
          title={<>У модели <span style={{ color: 'var(--frm-accent)', fontWeight: 800 }}>нет памяти</span>. При каждом новом запросе агент начинает с чистого листа.</>}
        >
          Иллюзию памяти создаёт контекст — всё, что агенту нужно знать, мы заново собираем и передаём ему при каждом запросе. Агент знает ровно то, что вошло в окно этого запроса.
        </Callout>
      </GuidePanel>

      <GuidePanel title="Каждый запрос — с чистого листа" description="что происходит на одном обращении к агенту">
        <div className="help-context-flow" style={FLOW_GRID_STYLE}>
          <FlowCard title="Новый запрос" description="вопрос или команда владельца" tone="#38BDF8" />
          <ArrowCell />
          <FlowCard title="Контекст собирается заново" description="из vault — не из чьей-то памяти" tone="#34D399">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              <LayerChip color="#34D399">переписка</LayerChip>
              <LayerChip color="#818CF8">sprint.context</LayerChip>
              <LayerChip color="#38BDF8">скилы</LayerChip>
            </div>
          </FlowCard>
          <ArrowCell />
          <FlowCard title="Модель" description="работает только с текущим окном" tone="#94A3B8">
            <Pill tone="danger" size="sm">без памяти</Pill>
          </FlowCard>
          <ArrowCell />
          <FlowCard title="Ответ" description="основан только на этом окне" tone="#34D399" />
        </div>
        <Surface padding="md" variant="subtle">
          <span style={GUIDE_TEXT_STYLE}><strong style={{ color: 'var(--frm-danger)' }}>Следующий запрос повторяет всё заново.</strong> Если факт не попал в собранный контекст, для агента его не существует.</span>
        </Surface>
      </GuidePanel>

      <GuidePanel title="Каждый запрос = контекст + суть запроса" description="из чего собирается окно одного обращения">
        <div className="help-context-two" style={GUIDE_TWO_COLUMN_STYLE}>
          <div style={{ ...WELL_STYLE, display: 'grid', gap: 9, padding: 18 }}>
            <div style={ROW_STYLE}>
              <span style={{ color: 'var(--frm-text-muted)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>Окно одного запроса</span>
              <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11 }}>собирается заново</span>
            </div>
            <WindowLayer title="Скилы" note="поведение платформы" color="#38BDF8" />
            <WindowLayer title="Контекст проекта" note="context.md · docs · zones" color="#38BDF8" mono />
            <WindowLayer title="Спринт" note="roadmap.md" color="#818CF8" mono />
            <WindowLayer title="Переписка" note="накопленная за сессию" color="#34D399" />
            <div style={{ alignItems: 'center', color: 'var(--frm-text-muted)', display: 'flex', gap: 6 }}>
              <span style={{ background: 'rgba(148,163,184,.14)', flex: 1, height: 1 }} />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>и только теперь</span>
              <span style={{ background: 'rgba(148,163,184,.14)', flex: 1, height: 1 }} />
            </div>
            <WindowLayer title="Суть текущего запроса" note="последний кусок" color="#38BDF8" accent />
          </div>
          <div style={STACK_STYLE}>
            <p style={GUIDE_TEXT_STYLE}>Сам запрос владельца — это только последний кусок окна. Всё, что выше него, агент не помнит: это собирается из vault и подставляется перед каждым запросом.</p>
            <ul style={{ display: 'grid', gap: 11, listStyle: 'none', margin: 0, padding: 0 }}>
              <Bullet><strong>Контекст пересобирается каждый раз.</strong> Нет фоновой памяти — есть пакет, который формируется на каждом запросе.</Bullet>
              <Bullet><strong>Запрос опирается только на то, что в окне.</strong> Если факт не подставлен, для агента его нет.</Bullet>
              <Bullet><strong>Постоянное живёт в vault, временное — в окне.</strong> Vault хранит знание, окно даёт рабочую память на один запрос.</Bullet>
            </ul>
          </div>
        </div>
      </GuidePanel>

      <ConversationGrowth />
      <WhyControl />
      <ContextAlgebra />
      <ContextMeter />
      <Budgets />
      <OverflowAndTriggers />
      <ContextResetGuide />

      <GuidePanel title="Исходный markdown" description="Read-only выдержка из docs/ и skills/, которые используются для справки.">
        {loading ? (
          <Surface padding="md" variant="subtle"><span style={MUTED_STYLE}>Загружаем live markdown...</span></Surface>
        ) : (
          <Markdown source={sourceMarkdown} />
        )}
      </GuidePanel>
    </div>
  )
}

export function HelpPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedSection = searchParams.get('section')
  const active: HelpSectionId = isHelpSectionId(requestedSection) ? requestedSection : DEFAULT_HELP_SECTION
  const section = getHelpSection(active)
  const helpQuery = useQuery({
    queryKey: ['help', active],
    queryFn: () => fetchHelpSection(active),
  })
  const selectSection = (next: HelpSectionId) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('section', next)
    setSearchParams(nextParams)
  }
  const renderSection = () => {
    if (active === 'start') {
      return <StartGuide sourceMarkdown={helpQuery.data?.markdown ?? ''} loading={helpQuery.isLoading} />
    }
    if (active === 'projects') {
      return <ProjectsGuide sourceMarkdown={helpQuery.data?.markdown ?? ''} loading={helpQuery.isLoading} />
    }
    if (active === 'sprints') {
      return <SprintsGuide sourceMarkdown={helpQuery.data?.markdown ?? ''} loading={helpQuery.isLoading} />
    }
    return <ContextGuide sourceMarkdown={helpQuery.data?.markdown ?? ''} loading={helpQuery.isLoading} />
  }

  return (
    <div style={PAGE_STYLE}>
      <PageHeader
        eyebrow="Live handbook"
        title="Справка"
        description="Руководство по проектам, спринтам, командам и алгебре контекста. Текст собирается из тех же docs/ и skills/, что участвуют в рабочем контексте."
        meta={<Pill tone={helpQuery.isFetching ? 'warning' : 'success'}>{helpQuery.isFetching ? 'загрузка' : 'актуально'}</Pill>}
      />
      <MetricStrip
        minColumnWidth={170}
        items={[
          { id: 'sections', label: 'Разделов', value: formatInteger(HELP_SECTIONS.length) },
          { id: 'source', label: 'Источники', value: 'docs + skills' },
          { id: 'section', label: 'Раздел', value: getHelpSection(active).title },
          { id: 'meter', label: 'Метр окна', value: '60/70/80/85%' },
        ]}
      />
      {helpQuery.isError ? (
        <ErrorBanner
          title="Не удалось загрузить раздел справки"
          description={helpQuery.error instanceof Error ? helpQuery.error.message : String(helpQuery.error)}
          action={<Button onClick={() => void helpQuery.refetch()}>Повторить</Button>}
        />
      ) : null}
      <div className="help-layout" style={LAYOUT_STYLE}>
        <aside className="help-nav" style={NAV_STYLE}>
          <div style={{ display: 'grid', gap: 5, padding: '0 4px' }}>
            <span style={{ color: 'var(--frm-accent)', fontSize: 10.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>Справка · /help</span>
            <h1 style={{ color: 'var(--frm-text-primary)', fontSize: 22, fontWeight: 800, letterSpacing: '-.015em', margin: 0 }}>Руководство</h1>
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 11.5 }}>read-only · docs/ + skills/</span>
          </div>
          <Surface padding="sm" variant="subtle" style={{ display: 'grid', gap: 6 }}>
            {HELP_SECTIONS.map((entry) => (
              <HelpNavButton key={entry.id} section={entry.id} active={entry.id === active} onSelect={selectSection} />
            ))}
          </Surface>
          <Surface padding="sm" variant="subtle">
            <span style={{ ...GUIDE_TEXT_STYLE, fontSize: 11.5 }}>
              Источник истины — <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent)' }}>vault</code>. Справка собирается из тех же документов и скилов, что нужны агенту в работе.
            </span>
          </Surface>
        </aside>
        <div style={STACK_STYLE}>
          <Surface className="help-mobile-section-select" padding="sm" variant="subtle">
            <Select
              aria-label="Раздел справки"
              label="Раздел справки"
              options={HELP_SECTION_OPTIONS}
              value={active}
              onChange={(event) => {
                const next = event.currentTarget.value
                if (isHelpSectionId(next)) selectSection(next)
              }}
            />
          </Surface>
          {renderSection()}
        </div>
      </div>
    </div>
  )
}
